import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  InstagramLogo,
  ShoppingBag,
  EnvelopeSimple,
  Eye,
  Tag,
  Gear,
  Sparkle,
} from "@phosphor-icons/react";

/**
 * GuidedTour v2 — pinned bottom-right product walkthrough.
 *
 * Physically navigates the user through every key tab so they SEE the
 * real UI behind a small persistent tour card (no backdrop, no blur).
 *
 * Eligibility (auto-opens once):
 *   1. user.plan is a paid tier (starter | premium)  AND
 *   2. user.needs_card === false  AND
 *   3. localStorage flag `id_tour_completed_v2` !== 'true'  AND
 *   4. localStorage flag `id_tour_dismissed_v2`  !== 'true'
 *
 * That set means: user finished checkout (free trial OR paid upgrade,
 * the SetupIntent + finalize-trial flow flips them off `trial_pending`
 * and clears `needs_card`).
 *
 * Persistence:
 *   - `id_tour_step_v2`        — current step index, survives page nav
 *   - `id_tour_active_v2`      — '1' once Welcome → "Show me" is hit so
 *     the card persists across the auto-navigations between tabs.
 *   - `id_tour_completed_v2`   — final, set on Finish.
 *   - `id_tour_dismissed_v2`   — set on Skip/X (won't re-open until
 *     a Settings → Replay tour click clears it).
 */

const KEY_STEP = "id_tour_step_v2";
const KEY_ACTIVE = "id_tour_active_v2";
const KEY_DONE = "id_tour_completed_v2";
const KEY_DISMISSED = "id_tour_dismissed_v2";

// Each step lives at a specific route. Clicking "Next" simultaneously
// (a) bumps the step index in localStorage and (b) navigates to that
// step's route — the tour card on the new page reads the saved step
// index and renders the matching content. Step 0 (welcome) lives
// wherever the user happens to land first (usually `/`).
const STEPS = [
  {
    key: "welcome",
    eyebrow: "Welcome aboard",
    icon: Sparkle,
    title: "These are your detected influencers.",
    body:
      "Let's take 60 seconds to walk through every feature on the actual page so you can use them right away.",
    route: null,        // stay where the user is (/influencers)
    primary: "Show me",
  },
  {
    key: "ig-followers",
    eyebrow: "Step 1 · Highest-leverage",
    icon: InstagramLogo,
    title: "Upload your Instagram follower list",
    body:
      "Drop an HTML file from your Instagram follower export. We cross-reference every follower against your customer list — every influencer who already follows your brand gets auto-tagged in seconds.",
    valueLine: "One-time upload. Pays back forever. Most brands skip it.",
    route: "/social-followers",
    primary: "Next",
  },
  {
    key: "auto-detection",
    eyebrow: "Step 2 · Always on",
    icon: ShoppingBag,
    title: "Every new order is auto-scanned",
    body:
      "From the moment your store is connected, every order is checked against Instagram, TikTok, YouTube and X. Influencers (1k+ followers, verified, or in our creator graph) show up here with confidence scores and DM-ready handles.",
    valueLine: "You don't lift a finger. Detected influencers land on this feed within 60 seconds of checkout.",
    route: "/influencers",
    primary: "Next",
  },
  {
    key: "subscribers",
    eyebrow: "Step 3 · Premium",
    icon: EnvelopeSimple,
    title: "Influencers from your email list",
    body:
      "Subscribed-but-haven't-bought creators are the easiest conversion. Drop your Klaviyo / Mailchimp subscriber list and we cross-match every email against social — flagging the high-reach ones.",
    valueLine: "Average store finds 30–80 high-reach creators on their existing list within 5 minutes.",
    route: "/potential-influencers",
    primary: "Next",
  },
  {
    key: "watchlist",
    eyebrow: "Step 4 · Premium",
    icon: Eye,
    title: "Watchlist — track creators 24/7",
    body:
      "Add any creator's email or @handle. We'll alert you the moment they order from you.",
    valueLine: "Best for lapsed VIPs, gifted creators, and anyone you're nurturing toward a paid partnership.",
    route: "/watchlist",
    primary: "Next",
  },
  {
    key: "outreach",
    eyebrow: "Step 5 · Outreach",
    icon: Tag,
    title: "Status, tags & DM templates",
    body:
      "Every detected influencer has a one-click status (Detected → Contacted → Partnered → Posted), custom tags (#wellness, #VIP), and pre-loaded DM templates so you move from cold to converted in 4 clicks.",
    valueLine: "Your CRM for influencer ops — no spreadsheets needed.",
    route: "/influencers",
    primary: "Next",
  },
  {
    key: "settings",
    eyebrow: "Step 6 · Setup",
    icon: Gear,
    title: "Alerts & WP plugin",
    body:
      "Set follower-count thresholds and fine-tune auto-tag rules. WooCommerce stores grab the WordPress plugin from here to deepen the order webhook (including IG handles in order notes).",
    valueLine: "Spend 5 minutes here now and the next 90 days run on autopilot.",
    route: "/settings",
    primary: "Finish tour",
  },
];

function readStep() {
  try {
    const v = parseInt(localStorage.getItem(KEY_STEP) || "0", 10);
    return Number.isFinite(v) && v >= 0 && v < STEPS.length ? v : 0;
  } catch {
    return 0;
  }
}

function readFlag(k) {
  try { return localStorage.getItem(k) === "true" || localStorage.getItem(k) === "1"; }
  catch { return false; }
}

export default function GuidedTour({ user }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [stepIdx, setStepIdx] = useState(() => readStep());
  const [open, setOpen] = useState(false);

  // Eligibility — only fires once for paid users with a card on file.
  const eligible = useMemo(() => {
    if (!user) return false;
    const plan = (user.plan || "").toLowerCase();
    const isPaid = plan === "starter" || plan === "premium" || plan === "paid";
    if (!isPaid) return false;
    if (user.needs_card) return false;
    if (user.trial_blocked) return false;
    if (readFlag(KEY_DONE)) return false;
    if (readFlag(KEY_DISMISSED)) return false;

    // Once the walkthrough is active (user clicked "Show me" on step 0),
    // render the tour on every destination tab so we can walk them
    // through. Hard-exclude only auth/checkout/paywall pages where a
    // floating card would interfere.
    const path = location.pathname;
    const blocked =
      path.startsWith("/checkout") ||
      path.startsWith("/login") ||
      path.startsWith("/signup") ||
      path.startsWith("/trial-blocked");
    if (blocked) return false;

    if (readFlag(KEY_ACTIVE)) return true;

    // First-time auto-open: only on /influencers. The dashboard (/) is
    // where the "find your first influencer" dopamine reveal lives —
    // putting the tour there would crash into the reveal animation.
    // The reveal's primary CTA navigates to /influencers, so by the
    // time the user lands here the reveal moment is over and the tour
    // is the natural next thing they see.
    return path === "/influencers";
  }, [user, location.pathname]);

  // Open the tour shortly after eligibility flips true OR if a previous
  // step had us mid-walkthrough (active flag set, just navigated).
  useEffect(() => {
    if (!eligible) {
      setOpen(false);
      return;
    }
    const active = readFlag(KEY_ACTIVE);
    // If active, render immediately so card stays visible across nav.
    // Otherwise wait ~700ms so the dashboard's first paint completes.
    const delay = active ? 0 : 700;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [eligible]);

  // Sync step from storage when location changes (every navigation).
  // This handles the case where step N's "Next" clicked → navigate →
  // new page mounts → reads next step index from storage.
  useEffect(() => {
    setStepIdx(readStep());
  }, [location.pathname]);

  const close = useCallback((markDone) => {
    setOpen(false);
    try {
      if (markDone) localStorage.setItem(KEY_DONE, "true");
      else localStorage.setItem(KEY_DISMISSED, "true");
      localStorage.removeItem(KEY_ACTIVE);
      localStorage.removeItem(KEY_STEP);
    } catch {}
  }, []);

  const goToStep = useCallback((nextIdx) => {
    if (nextIdx >= STEPS.length) {
      close(true);
      return;
    }
    try {
      localStorage.setItem(KEY_STEP, String(nextIdx));
      localStorage.setItem(KEY_ACTIVE, "1");
    } catch {}
    setStepIdx(nextIdx);
    const target = STEPS[nextIdx].route;
    if (target && target !== location.pathname) {
      navigate(target);
    }
  }, [navigate, location.pathname, close]);

  if (!open) return null;

  const step = STEPS[stepIdx];
  const Icon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <div
      className="gt-card"
      role="dialog"
      aria-modal="false"
      aria-label="Get started tour"
      data-testid="guided-tour"
    >
      <TourStyles />
      <div className="gt-inner" data-testid={`guided-tour-step-${step.key}`}>
        <button
          className="gt-close"
          aria-label="Close tour"
          data-testid="guided-tour-close"
          onClick={() => close(false)}
          type="button"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="gt-head">
          <span className="gt-eyebrow">{step.eyebrow}</span>
          <span className="gt-progress" data-testid="guided-tour-progress">
            {stepIdx + 1} / {STEPS.length}
          </span>
        </div>

        <div className="gt-icon-wrap">
          <Icon size={26} weight="duotone" />
        </div>

        <h3 className="gt-title">{step.title}</h3>
        <p className="gt-body">{step.body}</p>
        {step.valueLine && (
          <div className="gt-value">
            <CheckCircle size={13} weight="fill" />
            <span>{step.valueLine}</span>
          </div>
        )}

        <button
          type="button"
          className="gt-btn-primary"
          data-testid="guided-tour-primary"
          onClick={() => goToStep(stepIdx + 1)}
        >
          {step.primary}
          {!isLast && <ArrowRight size={13} weight="bold" />}
        </button>

        <div className="gt-foot">
          <button
            type="button"
            className="gt-link"
            disabled={stepIdx === 0}
            onClick={() => goToStep(Math.max(0, stepIdx - 1))}
            data-testid="guided-tour-back"
          >
            <ArrowLeft size={11} weight="bold" /> Back
          </button>
          <span className="gt-dots">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`gt-dot ${i === stepIdx ? "is-active" : ""} ${i < stepIdx ? "is-done" : ""}`}
              />
            ))}
          </span>
          {!isLast ? (
            <button
              type="button"
              className="gt-link"
              onClick={() => close(false)}
              data-testid="guided-tour-skip"
            >
              Skip
            </button>
          ) : (
            <span className="gt-link gt-link-quiet">Last step</span>
          )}
        </div>
      </div>
    </div>
  );
}

const TourStyles = () => (
  <style>{`
    .gt-card {
      position: fixed;
      bottom: 24px;
      right: 24px;
      width: 360px;
      max-width: calc(100vw - 32px);
      z-index: 9000;
      animation: gt-slide-in 280ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
      pointer-events: none;
    }
    .gt-inner {
      position: relative;
      background: #0F1410;
      border: 1px solid rgba(201, 247, 63, 0.22);
      border-radius: 14px;
      padding: 22px 22px 16px;
      box-shadow:
        0 18px 40px rgba(0,0,0,0.35),
        0 6px 14px rgba(0,0,0,0.18),
        0 0 0 1px rgba(255,255,255,0.04);
      color: #F5F5F5;
      font-family: -apple-system, BlinkMacSystemFont, "Inter", "SF Pro Text", sans-serif;
      pointer-events: auto;
    }
    @keyframes gt-slide-in {
      0%   { transform: translateY(16px) translateX(8px); opacity: 0; }
      100% { transform: translateY(0) translateX(0); opacity: 1; }
    }

    .gt-close {
      position: absolute; top: 12px; right: 12px;
      width: 24px; height: 24px;
      display: inline-flex; align-items: center; justify-content: center;
      background: transparent; border: none; cursor: pointer;
      color: rgba(255,255,255,0.45); border-radius: 5px;
      transition: background-color 150ms ease, color 150ms ease;
    }
    .gt-close:hover { background: rgba(255,255,255,0.06); color: #fff; }

    .gt-head {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 12px;
      padding-right: 24px;
    }
    .gt-eyebrow {
      font-size: 10px; font-weight: 700; letter-spacing: 0.10em;
      text-transform: uppercase; color: #C9F73F;
    }
    .gt-progress {
      font-size: 10px; color: rgba(255,255,255,0.45);
      font-variant-numeric: tabular-nums;
    }

    .gt-icon-wrap {
      width: 42px; height: 42px;
      display: inline-flex; align-items: center; justify-content: center;
      background: rgba(201, 247, 63, 0.10);
      border: 1px solid rgba(201, 247, 63, 0.22);
      color: #C9F73F;
      border-radius: 10px;
      margin-bottom: 12px;
    }

    .gt-title {
      font-size: 16px; font-weight: 700;
      letter-spacing: -0.01em;
      color: #FFFFFF;
      margin: 0 0 8px;
      line-height: 1.3;
    }
    .gt-body {
      font-size: 13px; line-height: 1.55;
      color: rgba(255,255,255,0.72);
      margin: 0 0 12px;
    }
    .gt-value {
      display: flex; align-items: flex-start; gap: 7px;
      padding: 9px 11px;
      background: rgba(201, 247, 63, 0.06);
      border: 1px solid rgba(201, 247, 63, 0.18);
      border-radius: 7px;
      font-size: 11.5px; line-height: 1.45;
      color: rgba(255,255,255,0.85);
      margin-bottom: 14px;
    }
    .gt-value svg { color: #C9F73F; flex-shrink: 0; margin-top: 2px; }

    .gt-btn-primary {
      width: 100%;
      display: inline-flex; align-items: center; justify-content: center;
      gap: 6px;
      padding: 11px 16px;
      border-radius: 8px;
      border: none; cursor: pointer;
      background: #C9F73F; color: #0A0A0A;
      font-size: 13px; font-weight: 600;
      letter-spacing: -0.01em;
      font-family: inherit;
      box-shadow: 0 1px 0 rgba(0,0,0,0.06), 0 0 0 1px rgba(201, 247, 63, 0.4);
      transition: transform 120ms ease, background-color 150ms ease;
      margin-bottom: 12px;
    }
    .gt-btn-primary:hover { background: #B7E62F; }
    .gt-btn-primary:active { transform: translateY(1px); }

    .gt-foot {
      display: grid; grid-template-columns: auto 1fr auto;
      align-items: center; gap: 10px;
      padding-top: 12px;
      border-top: 1px solid rgba(255,255,255,0.06);
    }
    .gt-link {
      display: inline-flex; align-items: center; gap: 3px;
      background: transparent; border: none; cursor: pointer;
      font-size: 11px; color: rgba(255,255,255,0.5);
      font-family: inherit;
      transition: color 150ms ease;
      padding: 3px 5px;
    }
    .gt-link:hover:not(:disabled) { color: #fff; }
    .gt-link:disabled { opacity: 0.3; cursor: not-allowed; }
    .gt-link-quiet { color: rgba(255,255,255,0.3); }
    .gt-dots {
      display: flex; gap: 4px; justify-content: center;
    }
    .gt-dot {
      width: 4px; height: 4px; border-radius: 50%;
      background: rgba(255,255,255,0.2);
      transition: background 200ms ease, transform 200ms ease;
    }
    .gt-dot.is-active { background: #C9F73F; transform: scale(1.5); }
    .gt-dot.is-done { background: rgba(201, 247, 63, 0.45); }

    @media (max-width: 600px) {
      .gt-card {
        bottom: 12px;
        right: 12px;
        left: 12px;
        width: auto;
      }
    }
  `}</style>
);
